import type {
  PpmpItemCatalogOption,
  ProcurementCatalogOption,
} from '../procurementCatalogTypes';

/**
 * What Autogenerate fills into one row of the procurement-line grid.
 *
 * The field names are the draft row's, so the modal spreads it. It is deliberately a PARTIAL:
 * the row's `key` is the grid's own and a generator must never mint one, or removing a row
 * would stop matching.
 */
export interface SamplePpmpLine {
  ppmpItemCatalogId: string | null;
  descriptionHtml: string | null;
  descriptionText: string;
  procurementTypeId: string | null;
  modeOfProcurementId: string | null;
  quantity: number;
  unit: string;
  estimatedBudget: number;
  procurementStartDate: string;
  procurementEndDate: string;
  expectedDeliveryDate: string;
  remarks: string;
}

/**
 * A procurement window and a delivery date, in order, starting a few months out.
 *
 * Computed in UTC off today's y/m/d for the reason {@link milestoneDates} is: these are
 * CALENDAR dates, and the instant path moves them a day for every reader west of UTC.
 *
 * The PRE-PROC CONFERENCE flag is deliberately not generated — it states something about how
 * the procurement is run, and a generator must not answer that on the department's behalf.
 */
function sampleWindow(): { start: string; end: string; delivery: string } {
  const today = new Date();
  const base = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const day = 86_400_000;
  const at = (days: number) => new Date(base + days * day).toISOString().slice(0, 10);

  const startIn = 30 + Math.floor(Math.random() * 60);

  return { start: at(startIn), end: at(startIn + 45), delivery: at(startIn + 75) };
}

/** Plausible vocabulary — none of it reaches `rsys_pii`, so it need not look synthetic. */
const ITEMS = [
  { text: 'Bond paper, A4, 70 gsm, substance 20', unit: 'ream', cost: 285 },
  { text: 'Desktop computer, i5, 16 GB RAM, 512 GB SSD', unit: 'unit', cost: 48_500 },
  { text: 'Office chair, swivel, mid-back, fabric', unit: 'pc', cost: 6_750 },
  { text: 'Toner cartridge, black, high yield', unit: 'cartridge', cost: 4_200 },
  { text: 'Janitorial supplies, assorted', unit: 'lot', cost: 18_000 },
];

const pick = <T,>(rows: readonly T[]): T => rows[Math.floor(Math.random() * rows.length)];

/**
 * Fills ONE procurement line.
 *
 * **It takes the catalogs as an argument**, because a generator that invented ids would
 * produce a row the server refuses — which reads as a bug in the form rather than as a
 * generator doing its job. A parameterless one silently fills none of the foreign keys.
 *
 * **The CATALOGUE entry is used where one exists**, so the generated line carries a real
 * snapshot and a real id; with an empty catalog it falls back to typing a description, which
 * is exactly what a reader does.
 */
export function samplePpmpLine(
  types: ProcurementCatalogOption[],
  modes: ProcurementCatalogOption[],
  catalog: PpmpItemCatalogOption[],
): SamplePpmpLine {
  const window = sampleWindow();

  if (catalog.length > 0) {
    const chosen = pick(catalog);

    return {
      remarks: 'Generated for testing.',
      ppmpItemCatalogId: chosen.id,
      descriptionHtml: chosen.itemDescription,
      descriptionText: chosen.itemDescriptionText,
      procurementTypeId: types.length > 0 ? pick(types).id : null,
      modeOfProcurementId: modes.length > 0 ? pick(modes).id : null,
      quantity: 1 + Math.floor(Math.random() * 20),
      unit: chosen.unit,
      estimatedBudget: chosen.estimatedUnitCost ?? 1_000 + Math.floor(Math.random() * 50_000),
      procurementStartDate: window.start,
      procurementEndDate: window.end,
      expectedDeliveryDate: window.delivery,
    };
  }

  const item = pick(ITEMS);
  const quantity = 1 + Math.floor(Math.random() * 20);

  return {
    remarks: 'Generated for testing.',
    ppmpItemCatalogId: null,
    // PLAIN TEXT, which the server sanitizes to itself — the grid cannot author markup, and a
    // generator must not write a value the form could not have produced.
    descriptionHtml: null,
    descriptionText: item.text,
    procurementTypeId: types.length > 0 ? pick(types).id : null,
    modeOfProcurementId: modes.length > 0 ? pick(modes).id : null,
    quantity,
    unit: item.unit,
    estimatedBudget: item.cost * quantity,
    procurementStartDate: window.start,
    procurementEndDate: window.end,
    expectedDeliveryDate: window.delivery,
  };
}

/**
 * Four bidding milestones, IN ORDER, inside a plan's own coverage window.
 *
 * **Computed in UTC off the y/m/d, never through the instant path** — a coverage window is a
 * pair of CALENDAR dates, and putting one through a local-timezone `Date` moves a plan starting
 * 01 January to 31 December for every reader west of UTC.
 *
 * The four land at roughly a tenth, a quarter, a half and two thirds of the window, so the
 * generated record reads as a schedule rather than as four values on the same day.
 */
export function milestoneDates(
  coverageStart: string,
  coverageEnd: string,
): [string, string, string, string] {
  const start = Date.parse(`${coverageStart}T00:00:00Z`);
  const end = Date.parse(`${coverageEnd}T00:00:00Z`);

  // A window the server could not have produced — reversed, or either date unreadable — yields
  // no dates at all rather than a schedule nobody can explain. They are optional fields.
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return ['', '', '', ''];
  }

  const span = end - start;
  const at = (fraction: number) =>
    new Date(start + Math.round(span * fraction)).toISOString().slice(0, 10);

  return [at(0.1), at(0.25), at(0.5), at(0.67)];
}

/**
 * How many departments Autogenerate will ask about before giving up.
 *
 * A roster can hold every office in the chart and most of them have no PPA under a given plan,
 * so the generator probes rather than guesses — but one click must not become forty requests.
 */
export const MAX_GENERATE_PROBES = 6;

/** A copy in random order. The source array is the picker's own and must not be reordered. */
export function shuffled<T>(rows: readonly T[]): T[] {
  const copy = [...rows];

  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }

  return copy;
}
